import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { RefDataProvider } from "@/context/RefDataContext";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/AppLayout";
import AuthCallback from "@/pages/AuthCallback";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Locations from "@/pages/Locations";
import Trainers from "@/pages/Trainers";
import Clients from "@/pages/Clients";
import Dogs from "@/pages/Dogs";
import Programs from "@/pages/Programs";
import ProgramDetail from "@/pages/ProgramDetail";
import Classes from "@/pages/Classes";
import Homework from "@/pages/Homework";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Attendance from "@/pages/Attendance";
import Bookings from "@/pages/Bookings";
import Progress from "@/pages/Progress";
import NetworkHealth from "@/pages/NetworkHealth";
import LocationDetail from "@/pages/LocationDetail";
import ClientDetail from "@/pages/ClientDetail";
import TrainerDetail from "@/pages/TrainerDetail";
import ClassDetail from "@/pages/ClassDetail";
import ProgressNotes from "@/pages/ProgressNotes";
import TrainerDashboard from "@/pages/TrainerDashboard";
import ClientDogDetail from "@/pages/ClientDogDetail";
import ClientProfile from "@/pages/ClientProfile";
import Quality from "@/pages/Quality";
import QualityIssueDetail from "@/pages/QualityIssueDetail";
import Academy from "@/pages/Academy";
import AcademyCourseDetail from "@/pages/AcademyCourseDetail";
import AcademyEnrollment from "@/pages/AcademyEnrollment";
import Payments from "@/pages/Payments";
import AI from "@/pages/AI";
import Analytics from "@/pages/Analytics";
import { PawPrint } from "lucide-react";

function Loader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-12 w-12 rounded-xl bg-primary flex items-center justify-center animate-pulse">
        <PawPrint size={26} className="text-primary-foreground" />
      </div>
    </div>
  );
}

function ProtectedShell() {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <RefDataProvider>
      <AppLayout><Outlet /></AppLayout>
    </RefDataProvider>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<ProtectedShell />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/locations" element={<Locations />} />
        <Route path="/locations/:id" element={<LocationDetail />} />
        <Route path="/trainers" element={<Trainers />} />
        <Route path="/trainers/:id" element={<TrainerDetail />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/:id" element={<ClientDetail />} />
        <Route path="/dogs" element={<Dogs />} />
        <Route path="/dogs/:id" element={<ClientDogDetail />} />
        <Route path="/profile" element={<ClientProfile />} />
        <Route path="/programs" element={<Programs />} />
        <Route path="/programs/:id" element={<ProgramDetail />} />
        <Route path="/classes" element={<Classes />} />
        <Route path="/homework" element={<Homework />} />
        <Route path="/attendance" element={<Attendance />} />
        <Route path="/bookings" element={<Bookings />} />
        <Route path="/progress" element={<Progress />} />
        <Route path="/trainer" element={<TrainerDashboard />} />
        <Route path="/classes/:id" element={<ClassDetail />} />
        <Route path="/progress-notes" element={<ProgressNotes />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/quality" element={<Quality />} />
        <Route path="/quality/issues/:id" element={<QualityIssueDetail />} />
        <Route path="/academy" element={<Academy />} />
        <Route path="/academy/courses/:id" element={<AcademyCourseDetail />} />
        <Route path="/academy/enrollments/:id" element={<AcademyEnrollment />} />
        <Route path="/payments" element={<Payments />} />
        <Route path="/ai" element={<AI />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/network-health" element={<NetworkHealth />} />
        <Route path="/settings" element={<Settings />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
